import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../services/api';
import toast from 'react-hot-toast';

const PALETAS = [
  { label: 'Azul Clássico',   cor: '#1e40af' },
  { label: 'Verde Floresta',  cor: '#166534' },
  { label: 'Vermelho Tijolo', cor: '#9f1239' },
  { label: 'Roxo',            cor: '#6b21a8' },
  { label: 'Laranja Obra',    cor: '#92400e' },
  { label: 'Teal',            cor: '#0f766e' },
  { label: 'Cinza Executivo', cor: '#1f2937' },
];

// Dimensões máximas da logo no PDF (em px equivalente)
const MAX_W = 400;
const MAX_H = 120;

function formatarCNPJ(v) {
  const n = v.replace(/\D/g, '').slice(0, 14);
  if (n.length <= 2)  return n;
  if (n.length <= 5)  return `${n.slice(0,2)}.${n.slice(2)}`;
  if (n.length <= 8)  return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8)}`;
  return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8,12)}-${n.slice(12)}`;
}

async function aplicarCrop(cropSrc, imgEl, pixelCrop) {
  const scaleX = imgEl.naturalWidth  / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;
  const srcX   = Math.round(pixelCrop.x      * scaleX);
  const srcY   = Math.round(pixelCrop.y      * scaleY);
  const srcW   = Math.round(pixelCrop.width  * scaleX);
  const srcH   = Math.round(pixelCrop.height * scaleY);

  const ratio = srcW / srcH;
  let outW = srcW;
  let outH = srcH;
  if (outW > MAX_W) { outW = MAX_W; outH = outW / ratio; }
  if (outH > MAX_H) { outH = MAX_H; outW = outH * ratio; }

  // createImageBitmap decodifica os bytes brutos do PNG sem compositing do browser,
  // preservando o canal alpha que ctx.drawImage(imgEl) pode perder.
  const blob = await fetch(cropSrc).then(r => r.blob());
  const bmp  = await createImageBitmap(blob, srcX, srcY, srcW, srcH);

  const canvas = document.createElement('canvas');
  canvas.width  = Math.round(outW);
  canvas.height = Math.round(outH);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close();

  return { base64: canvas.toDataURL('image/png'), ratio: parseFloat((outW / outH).toFixed(4)) };
}

function Empresa() {
  const [empresaNome, setEmpresaNome] = useState('');
  const [cnpj,        setCnpj]        = useState('');
  const [cor,         setCor]         = useState('#1e40af');
  const [logo,        setLogo]        = useState(null);
  const [logoRatio,   setLogoRatio]   = useState(3);
  const [salvando,    setSalvando]    = useState(false);

  // Crop
  const [cropSrc,        setCropSrc]        = useState(null);
  const [crop,           setCrop]           = useState({ unit: '%', x: 5, y: 5, width: 90, height: 90 });
  const [completedCrop,  setCompletedCrop]  = useState(null);
  const imgCropRef = useRef(null);
  const fileRef    = useRef(null);

  useEffect(() => {
    api.get('/configuracoes-sistema').then(r => {
      const d = r.data;
      if (d.empresa_nome)          setEmpresaNome(d.empresa_nome);
      if (d.empresa_cnpj)          setCnpj(d.empresa_cnpj);
      if (d.empresa_cor_primaria)  setCor(d.empresa_cor_primaria);
      if (d.empresa_logo)          { setLogo(d.empresa_logo); setLogoRatio(parseFloat(d.empresa_logo_ratio || 3)); }
    }).catch(() => {});
  }, []);

  const handleArquivo = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'image/png') {
      toast.error('Use apenas PNG para garantir fundo transparente.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      setCrop({ unit: '%', x: 5, y: 5, width: 90, height: 90 });
      setCompletedCrop(null);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  async function confirmarCrop() {
    if (!imgCropRef.current) return;
    const c = completedCrop || convertToPixelCrop(crop, imgCropRef.current.width, imgCropRef.current.height);
    if (!c || c.width < 10 || c.height < 10) {
      toast.error('Selecione uma área maior para recortar.');
      return;
    }
    const pixelCrop = c.unit === '%'
      ? convertToPixelCrop(c, imgCropRef.current.width, imgCropRef.current.height)
      : c;
    try {
      const { base64, ratio } = await aplicarCrop(cropSrc, imgCropRef.current, pixelCrop);
      setLogo(base64);
      setLogoRatio(ratio);
      setCropSrc(null);
      toast.success('Logo recortada! Salve para aplicar.');
    } catch {
      toast.error('Erro ao processar a imagem.');
    }
  }

  async function removerLogo() {
    setLogo(null);
    await Promise.all([
      api.put('/configuracoes-sistema/empresa_logo',       { valor: '' }),
      api.put('/configuracoes-sistema/empresa_logo_ratio', { valor: '' }),
    ]).catch(() => {});
    toast.success('Logo removida.');
  }

  async function salvar() {
    setSalvando(true);
    try {
      await Promise.all([
        api.put('/configuracoes-sistema/empresa_nome',          { valor: empresaNome.trim() }),
        api.put('/configuracoes-sistema/empresa_cnpj',          { valor: cnpj }),
        api.put('/configuracoes-sistema/empresa_cor_primaria',  { valor: cor }),
        api.put('/configuracoes-sistema/empresa_logo',          { valor: logo || '' }),
        api.put('/configuracoes-sistema/empresa_logo_ratio',    { valor: String(logoRatio) }),
      ]);
      toast.success('Configurações da empresa salvas!');
    } catch {
      toast.error('Erro ao salvar configurações.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>🏢 Empresa</h2>

      {/* ── Logo ── */}
      <div className="card">
        <h3>Logo</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          Somente <strong>PNG</strong>, garante fundo transparente. Após selecionar, você pode recortar a imagem antes de salvar.
        </p>
        {logo ? (
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Sobre o cabeçalho</div>
                <div style={{ background: cor, padding: '8px 14px', borderRadius: 8, display: 'inline-flex', alignItems: 'center' }}>
                  <img src={logo} alt="Logo" style={{ maxHeight: 44, maxWidth: 160, display: 'block', objectFit: 'contain' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => fileRef.current.click()}>Trocar</button>
              <button className="btn-danger"    style={{ fontSize: 12, padding: '5px 10px' }} onClick={removerLogo}>Remover</button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => fileRef.current.click()}
            style={{
              border: '2px dashed var(--border-color)', borderRadius: 10,
              padding: '28px 24px', textAlign: 'center', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: 13, marginBottom: 4,
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--azul-medio)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
            <div style={{ fontWeight: 600 }}>Clique para enviar a logo</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Apenas PNG</div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/png" onChange={handleArquivo} style={{ display: 'none' }} />
      </div>

      {/* ── Dados ── */}
      <div className="card">
        <h3>Dados da Empresa</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          Exibidos no cabeçalho dos PDFs no lugar de "CronoObra by BRASZTECH".
        </p>
        <div className="grid-responsivo" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Nome da Empresa</label>
            <input value={empresaNome} onChange={e => setEmpresaNome(e.target.value)} placeholder="Ex: SP Engenharia Civil" style={{ marginBottom: 0 }} />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>CNPJ</label>
            <input value={cnpj} onChange={e => setCnpj(formatarCNPJ(e.target.value))} placeholder="00.000.000/0001-00" maxLength={18} style={{ marginBottom: 0 }} />
          </div>
        </div>
      </div>

      {/* ── Cor ── */}
      <div className="card">
        <h3>Cor do Relatório</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          Usada no cabeçalho, títulos de seção e cabeçalho das tabelas dos PDFs.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          {PALETAS.map(p => (
            <button
              key={p.cor}
              onClick={() => setCor(p.cor)}
              title={p.label}
              style={{
                width: 36, height: 36, borderRadius: 8, background: p.cor,
                border: cor === p.cor ? '3px solid var(--text-primary)' : '2px solid transparent',
                boxShadow: cor === p.cor ? '0 0 0 2px var(--bg-primary), 0 0 0 4px var(--text-primary)' : 'none',
                cursor: 'pointer', outline: 'none', transition: 'transform 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            type="color" value={cor}
            onChange={e => setCor(e.target.value)}
            style={{ width: 44, height: 36, padding: 2, borderRadius: 6, border: '1px solid var(--border-color)', cursor: 'pointer', background: 'none' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 52, height: 28, borderRadius: 6, background: cor, border: '1px solid var(--border-color)' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Prévia</span>
          </div>
        </div>

        {/* Prévia cabeçalho */}
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Como ficará o cabeçalho do relatório:</p>
          <div style={{ background: cor, borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14, minHeight: 64 }}>
            {logo && (
              <img src={logo} alt="Logo" style={{ maxHeight: 40, maxWidth: 120, objectFit: 'contain', flexShrink: 0 }} />
            )}
            <div>
              {empresaNome ? (
                <>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>{empresaNome.toUpperCase()}</div>
                  {cnpj && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>CNPJ: {cnpj}</div>}
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, marginTop: 3 }}>Obra: Nome da Obra</div>
                </>
              ) : (
                <>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>CronoObra</div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>by BRASZTECH</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <button className="btn-primary" onClick={salvar} disabled={salvando}>
        {salvando ? 'Salvando...' : '💾 Salvar configurações'}
      </button>

      {/* ── Modal de Crop ── */}
      {cropSrc && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            background: 'var(--bg-primary)', borderRadius: 12,
            padding: 24, maxWidth: 680, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}>
            <h3 style={{ marginBottom: 4 }}>✂️ Recortar logo</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Arraste para selecionar a área que deseja usar como logo.
            </p>
            <div style={{ maxHeight: '60vh', overflow: 'auto', display: 'flex', justifyContent: 'center', background: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 0 0 / 16px 16px', borderRadius: 8 }}>
              <ReactCrop
                crop={crop}
                onChange={(_, pct) => setCrop(pct)}
                onComplete={(_, pct) => setCompletedCrop(pct)}
                keepSelection
              >
                <img
                  ref={imgCropRef}
                  src={cropSrc}
                  alt="Recortar"
                  style={{ maxWidth: '100%', maxHeight: '55vh', display: 'block' }}
                />
              </ReactCrop>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setCropSrc(null)}>Cancelar</button>
              <button className="btn-primary"   onClick={confirmarCrop}>Aplicar recorte</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Empresa;
